// =============================================================================
// sales.ts — Catálogo canônico do domínio comercial (agenda, tentativas de
// contato, campanhas e telefonia).
//
// Fonte única dos enums e metadados usados por todo o programa comercial
// (F32→F36). Nenhum slot subsequente redefine estes valores — schema
// (Postgres CHECK), API (`z.enum`), worker e frontend (`<select>`) importam
// todos daqui. Este módulo existe para não repetir o drift observado entre
// `apps/api/src/modules/tasks/schemas.ts` e
// `packages/shared-schemas/src/tasks.ts` (doc 26 §20, nota do F32-S01).
//
// Este arquivo NÃO define tabela, migration nem rota — apenas vocabulário
// fechado + metadados de desfecho. As decisões registradas aqui fecham
// doc 26 §23 itens 3 (resultados comerciais) e 7 (indicadores de contato e
// conversão); ver docs/26-agenda-comercial-campanhas-telefonia.md §26.
//
// LGPD: nenhum valor deste catálogo é ou pode conter texto livre do titular
// dos dados — são códigos técnicos/comerciais fechados. Observações de
// tentativas de contato (`contact_attempts.notes`) NÃO fazem parte deste
// módulo e devem ser tratadas como PII potencial pelo consumidor (doc 17).
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// appointment_status — ciclo de vida de um compromisso da agenda comercial.
// ---------------------------------------------------------------------------
/**
 * Status do compromisso de agenda comercial (`contact_appointments.status`).
 *
 * "Atrasado" é um estado CALCULADO (`scheduled_at < now()` com status ainda
 * `scheduled`), nunca persistido — doc 26 §8.
 */
export const APPOINTMENT_STATUSES = ['scheduled', 'completed', 'cancelled'];
export const AppointmentStatusSchema = z.enum(APPOINTMENT_STATUSES, {
    errorMap: () => ({ message: 'status de compromisso inválido' }),
});
// ---------------------------------------------------------------------------
// contact_channel — canal usado para registrar contato ou compromisso.
// ---------------------------------------------------------------------------
/**
 * Canal de contato comercial (`contact_attempts.channel`,
 * `contact_appointments.channel`).
 *
 * - `phone_manual`: ligação discada manualmente pelo agente (fora do adaptador de telefonia).
 * - `phone_integrated`: ligação orquestrada pelo adaptador de telefonia (F36).
 * - `whatsapp`: contato via conversa do live chat.
 * - `in_person`: atendimento presencial.
 * - `other`: canal não catalogado — o consumidor deve exigir observação.
 */
export const CONTACT_CHANNELS = [
    'phone_manual',
    'phone_integrated',
    'whatsapp',
    'in_person',
    'other',
];
export const ContactChannelSchema = z.enum(CONTACT_CHANNELS, {
    errorMap: () => ({ message: 'canal de contato inválido' }),
});
// ---------------------------------------------------------------------------
// outcome_code — desfecho comercial de uma tentativa de contato.
// ---------------------------------------------------------------------------
/**
 * Desfecho comercial de uma tentativa de contato
 * (`contact_attempts.outcome_code`, também usado em
 * `contact_appointments.outcome_code` quando o compromisso é concluído).
 *
 * Lista fechada nesta fase (F32). Uma futura tabela de outcomes
 * configuráveis por organização (F34-S02) pode substituir este enum por um
 * catálogo dinâmico — até lá, este é o único contrato válido e nenhum outro
 * módulo pode inventar valores paralelos.
 */
export const SALES_OUTCOME_CODES = [
    'no_answer',
    'busy',
    'invalid_number',
    'callback_requested',
    'interested',
    'proposal_sent',
    'converted',
    'not_interested',
    'do_not_contact',
];
export const SalesOutcomeCodeSchema = z.enum(SALES_OUTCOME_CODES, {
    errorMap: () => ({ message: 'desfecho comercial inválido' }),
});
/**
 * Metadados por outcome — o tipo `Record<SalesOutcomeCode, SalesOutcomeMeta>`
 * força o compilador a recusar o build se um outcome novo for adicionado a
 * `SALES_OUTCOME_CODES` sem entrada correspondente aqui (exaustividade em
 * tempo de compilação — nunca vira bug de runtime).
 */
export const SALES_OUTCOME_META = {
    no_answer: {
        label: 'Não atendeu',
        requiresNote: false,
        requiresAppointment: false,
        terminatesMember: false,
        blocksContact: false,
        blocksContactScope: null,
    },
    busy: {
        label: 'Ocupado',
        requiresNote: false,
        requiresAppointment: false,
        terminatesMember: false,
        blocksContact: false,
        blocksContactScope: null,
    },
    invalid_number: {
        label: 'Número inválido',
        requiresNote: false,
        requiresAppointment: false,
        terminatesMember: false,
        blocksContact: true,
        blocksContactScope: 'phone_only',
    },
    callback_requested: {
        label: 'Pediu retorno',
        requiresNote: false,
        requiresAppointment: true,
        terminatesMember: false,
        blocksContact: false,
        blocksContactScope: null,
    },
    interested: {
        label: 'Interessado',
        requiresNote: false,
        requiresAppointment: false,
        terminatesMember: false,
        blocksContact: false,
        blocksContactScope: null,
    },
    proposal_sent: {
        label: 'Proposta enviada',
        requiresNote: false,
        requiresAppointment: false,
        terminatesMember: false,
        blocksContact: false,
        blocksContactScope: null,
    },
    converted: {
        label: 'Convertido',
        requiresNote: false,
        requiresAppointment: false,
        terminatesMember: true,
        blocksContact: false,
        blocksContactScope: null,
    },
    not_interested: {
        label: 'Sem interesse',
        requiresNote: true,
        requiresAppointment: false,
        terminatesMember: true,
        blocksContact: false,
        blocksContactScope: null,
    },
    do_not_contact: {
        label: 'Não contatar',
        requiresNote: true,
        requiresAppointment: false,
        terminatesMember: true,
        blocksContact: true,
        blocksContactScope: 'all_channels',
    },
};
/**
 * Outcomes de falha TÉCNICA de discagem — não chegaram a alcançar o titular.
 * Usado para calcular `EFFECTIVE_CONTACT_OUTCOMES` abaixo.
 */
const NON_EFFECTIVE_CONTACT_OUTCOMES = [
    'no_answer',
    'busy',
    'invalid_number',
];
/**
 * Outcomes considerados CONTATO EFETIVO para fins de indicador (doc 26 §23
 * item 7 — "indicadores oficiais de contato e conversão").
 *
 * Contato efetivo = a ligação/mensagem alcançou o titular e produziu um
 * desfecho comercial real, não apenas uma falha técnica de discagem
 * (`no_answer`, `busy`, `invalid_number` ficam de fora).
 */
export const EFFECTIVE_CONTACT_OUTCOMES = SALES_OUTCOME_CODES.filter((code) => !NON_EFFECTIVE_CONTACT_OUTCOMES.includes(code));
/**
 * Outcome que representa CONVERSÃO para fins de indicador (doc 26 §23 item
 * 7). Indicador oficial de conversão = `converted` / total de
 * `campaign_members` elegíveis (ou tentativas, conforme o relatório de
 * F35-S16) — a definição de denominador fica com o slot de relatórios; este
 * módulo só fixa qual outcome representa o numerador.
 */
export const CONVERSION_OUTCOME = 'converted';
// ---------------------------------------------------------------------------
// campaign_status — ciclo de vida de uma campanha comercial.
// ---------------------------------------------------------------------------
/** Status da campanha comercial (`sales_campaigns.status`). */
export const CAMPAIGN_STATUSES = [
    'draft',
    'ready',
    'active',
    'paused',
    'completed',
    'cancelled',
];
export const CampaignStatusSchema = z.enum(CAMPAIGN_STATUSES, {
    errorMap: () => ({ message: 'status de campanha inválido' }),
});
// ---------------------------------------------------------------------------
// campaign_member_status — estado de fila de um lead dentro de uma campanha.
// ---------------------------------------------------------------------------
/** Status de fila do membro de campanha (`campaign_members.status`). */
export const CAMPAIGN_MEMBER_STATUSES = [
    'pending',
    'available',
    'claimed',
    'waiting_callback',
    'completed',
    'blocked',
    'removed',
];
export const CampaignMemberStatusSchema = z.enum(CAMPAIGN_MEMBER_STATUSES, {
    errorMap: () => ({ message: 'status de membro de campanha inválido' }),
});
// ---------------------------------------------------------------------------
// assignment_strategy — regra de distribuição do público de uma campanha.
// ---------------------------------------------------------------------------
/**
 * Estratégia de atribuição de leads de uma campanha
 * (`sales_campaigns.assignment_strategy`).
 *
 * - `fixed_owner`: cada lead pertence a um vendedor fixo durante a campanha.
 * - `round_robin`: distribuição equilibrada e previsível entre vendedores.
 * - `shared_queue`: fila compartilhada — vendedores puxam o próximo item
 *   disponível (aquisição via `FOR UPDATE SKIP LOCKED`, doc 26 §8).
 */
export const ASSIGNMENT_STRATEGIES = ['fixed_owner', 'round_robin', 'shared_queue'];
export const AssignmentStrategySchema = z.enum(ASSIGNMENT_STRATEGIES, {
    errorMap: () => ({ message: 'estratégia de atribuição inválida' }),
});
// ---------------------------------------------------------------------------
// attempt_origin — o que disparou a tentativa de contato.
// ---------------------------------------------------------------------------
/**
 * Origem da tentativa de contato (`contact_attempts.origin`).
 *
 * `livechat` e `appointment` adicionados em F38-S01 (doc
 * planejamento-livechat-contexto-comercial.md §2): `livechat` identifica uma
 * tentativa registrada a partir do painel comercial do live chat (via
 * `RegisterContactDialog` com `origin='livechat'`); `appointment` identifica
 * a tentativa gerada automaticamente ao concluir um compromisso de agenda
 * (F38-S03, elo 4 do §3 — "concluir compromisso da Agenda → tentativa de
 * contato"), para não confundir com `manual` (o agente iniciou fora de
 * qualquer fila/compromisso).
 */
export const ATTEMPT_ORIGINS = [
    'manual',
    'click_to_call',
    'campaign_queue',
    'livechat',
    'appointment',
];
export const AttemptOriginSchema = z.enum(ATTEMPT_ORIGINS, {
    errorMap: () => ({ message: 'origem de tentativa inválida' }),
});
// ---------------------------------------------------------------------------
// call_technical_state — estado técnico da chamada, separado do outcome.
// ---------------------------------------------------------------------------
/**
 * Estado técnico da chamada telefônica, deliberadamente separado do
 * `SalesOutcomeCode` (doc 26 §4.3: "separar estado técnico da chamada de
 * resultado comercial"). Alimentado pelos webhooks do adaptador de
 * telefonia (F36).
 */
export const CALL_TECHNICAL_STATES = [
    'pending',
    'ringing_agent',
    'ringing_customer',
    'answered',
    'ended',
    'failed',
];
export const CallTechnicalStateSchema = z.enum(CALL_TECHNICAL_STATES, {
    errorMap: () => ({ message: 'estado técnico de chamada inválido' }),
});
// ---------------------------------------------------------------------------
// contact_preference_state — preferência de contato por lead/canal.
// ---------------------------------------------------------------------------
/**
 * Estado de preferência de contato (`contact_preferences.state`). Um
 * bloqueio ativo (`blocked`) sempre vence campanha, fila, agenda automática
 * e discador — doc 26 §8.
 */
export const CONTACT_PREFERENCE_STATES = ['allowed', 'blocked'];
export const ContactPreferenceStateSchema = z.enum(CONTACT_PREFERENCE_STATES, {
    errorMap: () => ({ message: 'preferência de contato inválida' }),
});
//# sourceMappingURL=sales.js.map